/**
 * Ajuda (todos os perfis): documentos de uso (manuais lidos aqui mesmo), contato por e-mail e chat.
 *  - área da equipe (`/org/:id/ajuda`): manuais conforme o papel + chat de suporte com a administração do EAPace;
 *  - área do atleta (`/org/:id/atleta/ajuda`): manual do atleta + atalho para o chat com a equipe da assessoria;
 *  - administração (`/admin/ajuda`): todos os manuais + atalho para a caixa de suporte.
 * Os manuais vêm de docs/manuais (scripts/sync-manuais.py) e são carregados só quando a página é aberta.
 */
import { useMemo, useState } from "react";
import { api } from "../../api/client";
import type { HelpInfo } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { IconChat } from "../../components/icons";
import { Button, EmptyState, ErrorState, Loading, PageHead, useToast } from "../../components/ui";
import { ROLE_LABEL } from "../../lib/format";
import { headingsOf, Markdown } from "../../lib/markdown";
import { useAsync, type AsyncState } from "../../lib/useAsync";
import type { Manual } from "../../help/manuais.generated";
import { Link } from "../../router/router";
import { UserTicketsSection } from "./TicketPages";
import { LOCALE, t, tj } from "../../i18n";
import { isApp } from "../../lib/platform";

export type HelpArea = "staff" | "athlete" | "admin";

const ORDER = ["administrador", "assessoria", "treinador", "atleta"];

function allowedSlugs(area: HelpArea, roles: string[]): string[] {
  if (area === "admin") return ORDER;
  if (area === "athlete") return ["atleta"];
  if (roles.includes("org_manager")) return ["assessoria", "treinador", "atleta"];
  return ["treinador", "atleta"];
}

function loadManuals() {
  return import("../../help/manuais.generated").then((m) => m.MANUAIS);
}

export function HelpPage({ area, base, orgId, slug }: { area: HelpArea; base: string; orgId?: string; slug?: string }) {
  const { me, membership, can } = useAuth();
  const roles = membership?.roles ?? [];
  const manuals = useAsync(loadManuals, []);
  const info = useAsync(() => api.get<HelpInfo>("/help"), []);
  const allowed = useMemo(() => allowedSlugs(area, roles), [area, roles.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (slug) return <ManualView slug={slug} allowed={allowed} base={base} state={manuals} />;

  const list = (manuals.data ?? []).filter((m) => allowed.includes(m.slug)).sort((a, b) => ORDER.indexOf(a.slug) - ORDER.indexOf(b.slug));
  const chatHere = area === "staff" && !!orgId && can("support.chat");

  return (
    <>
      <PageHead title={t("Ajuda")} subtitle={t("Manuais de uso e contato com a equipe do EAPace.")} />

      <section className="panel help-section" aria-labelledby="h-docs">
        <div className="panel-head">
          <h2 id="h-docs">{t("Documentos de uso")}</h2>
        </div>
        <div className="panel-body">
          {LOCALE !== "pt-BR" && (
            <p className="alert alert-info" role="note" lang={LOCALE}>
              {t("Os manuais de uso estão disponíveis só em português por enquanto.")}
            </p>
          )}
          {manuals.loading && !manuals.data ? (
            <Loading label={t("Carregando manuais…")} />
          ) : manuals.error ? (
            <ErrorState error={manuals.error} onRetry={manuals.reload} />
          ) : (
            <ul className="help-cards">
              {list.map((m) => (
                <li key={m.slug} className="help-card">
                  <h3>{m.title.replace(/\s+—\s+EAPace$/, "")}</h3>
                  <p className="muted">{m.description}</p>
                  <Link to={`${base}/manual/${m.slug}`} className="btn btn-secondary btn-sm">
                    {t("Abrir o manual")}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="panel help-section" aria-labelledby="h-contact">
        <div className="panel-head">
          <h2 id="h-contact">{t("Fale com a gente")}</h2>
        </div>
        <div className="panel-body help-contact">
          {area !== "admin" && me && <EmailCard info={info.data} name={me.full_name} email={me.email} orgName={membership?.organization_name} roles={roles} />}

          {area === "athlete" && orgId && (
            <div className="help-card">
              <h3>
                <IconChat width={18} height={18} /> {" "}{t("Chat com a minha equipe")}
              </h3>
              <p className="muted">
                {t("Dúvidas sobre treinos, planilha ou atividades vão para o seu treinador e a gestão da assessoria, pelas Mensagens do app. Você recebe o aviso no sino quando responderem.")}
              </p>
              <Link to={`/org/${orgId}/atleta/mensagens`} className="btn btn-primary btn-sm">
                {t("Abrir as mensagens")}
              </Link>
            </div>
          )}

          {area === "admin" && (
            <div className="help-card">
              <h3>
                <IconChat width={18} height={18} /> {" "}{t("Fila de suporte")}
              </h3>
              <p className="muted">{t("Os chamados de gestores e treinadores chegam na fila de suporte da plataforma.")}</p>
              <Link to="/admin/suporte" className="btn btn-primary btn-sm">
                {t("Abrir a fila de suporte")}
              </Link>
            </div>
          )}
        </div>

        {chatHere && orgId && <UserTicketsSection orgId={orgId} />}
      </section>

      <p className="muted help-version">EAPace {info.data?.app_version && info.data.app_version !== "dev" ? `v${info.data.app_version.replace(/^v/, "")}` : ""}</p>
    </>
  );
}

function EmailCard({
  info,
  name,
  email,
  orgName,
  roles,
}: {
  info: HelpInfo | null;
  name: string;
  email: string;
  orgName?: string;
  roles: string[];
}) {
  const toast = useToast();
  const to = info?.support_email ?? t("suporte@eapace.com");
  const version = info?.app_version && info.app_version !== "dev" ? `v${info.app_version.replace(/^v/, "")}` : "dev";
  const body = [
    t("Olá, equipe EAPace!"),
    "",
    t("(descreva aqui a sua dúvida ou o problema)"),
    "",
    "",
    t("--- Informações para o atendimento ---"),
    t("Nome: {name}", { name }),
    t("E-mail da conta: {email}", { email }),
    orgName ? t("Assessoria: {orgName}", { orgName }) : null,
    roles.length ? t("Perfil: {v}", { v: roles.map((r) => ROLE_LABEL[r] ?? r).join(", ") }) : null,
    t("Versão do EAPace: {version}", { version }),
    t("Navegador: {userAgent}", { userAgent: navigator.userAgent }),
  ]
    .filter((l) => l !== null)
    .join("\n");
  const href = `mailto:${to}?subject=${encodeURIComponent(t("[EAPace] Preciso de ajuda"))}&body=${encodeURIComponent(body)}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(to);
      toast(t("Endereço copiado."));
    } catch {
      toast(t("Endereço: {to}", { to }));
    }
  }

  return (
    <div className="help-card">
      <h3>{t("E-mail")}</h3>
      <p className="muted">
        {tj("Escreva para {to}. O botão abre o seu programa de e-mail já com os dados da sua conta, para o atendimento ser mais rápido.", {
          to: <strong>{to}</strong>,
        })}
      </p>
      <div className="help-actions">
        <a className="btn btn-primary btn-sm" href={href}>
          {t("Enviar e-mail")}
        </a>
        <Button size="sm" onClick={() => void copy()}>
          {t("Copiar endereço")}
        </Button>
      </div>
    </div>
  );
}

function ManualView({
  slug,
  allowed,
  base,
  state,
}: {
  slug: string;
  allowed: string[];
  base: string;
  state: AsyncState<Manual[]>;
}) {
  const manual = state.data?.find((m) => m.slug === slug);
  const toc = useMemo(() => (manual ? headingsOf(manual.text, 2) : []), [manual]);
  const [tocOpen, setTocOpen] = useState(false);

  if (!allowed.includes(slug)) {
    return (
      <>
        <PageHead title={t("Manual não disponível")} />
        <div className="panel">
          <EmptyState title={t("Este manual não está disponível para o seu perfil.")} action={<Link to={base}>{t("Voltar para a Ajuda")}</Link>} />
        </div>
      </>
    );
  }
  if (state.loading && !state.data) return <Loading label={t("Carregando manual…")} />;
  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (!manual) return <EmptyState title={t("Manual não encontrado.")} action={<Link to={base}>{t("Voltar para a Ajuda")}</Link>} />;

  return (
    <>
      <PageHead
        title={manual.title.replace(/\s+—\s+EAPace$/, "")}
        subtitle={<Link to={base}>{t("← Voltar para a Ajuda")}</Link>}
        actions={
          isApp ? undefined : (
            <Button size="sm" className="help-noprint" onClick={() => window.print()}>
              {t("Imprimir / salvar em PDF")}
            </Button>
          )
        }
      />
      {LOCALE !== "pt-BR" && (
        <p className="alert alert-info help-noprint" role="note" lang={LOCALE}>
          {t("Os manuais de uso estão disponíveis só em português por enquanto.")}
        </p>
      )}
      <div className="manual-layout" lang="pt-BR">
        {toc.length > 0 && (
          <nav className="manual-toc help-noprint" aria-label={t("Neste manual")}>
            <button className="manual-toc-toggle" aria-expanded={tocOpen} onClick={() => setTocOpen((o) => !o)}>
              {t("Neste manual")}
            </button>
            <ol className={tocOpen ? "open" : ""}>
              {toc.map((h) => (
                <li key={h.id}>
                  <a href={`#${h.id}`} onClick={() => setTocOpen(false)}>
                    {h.text}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}
        <article className="panel manual-body" aria-label={manual.title}>
          <div className="panel-body">
            <Markdown source={manual.text} skipTitle />
          </div>
        </article>
      </div>
    </>
  );
}
